import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { ArrowLeft, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '../ui/button';

const CartView = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getTotal, deliveryMode, routePrefix } = useCart();
  const { business } = useBusiness();

  const primaryColor = business?.primary_color || '#FFD700';
  const bgColor = business?.background_color || '#0D0D0D';
  const cardColor = business?.card_color || '#262626';
  const textColor = business?.text_color || '#FFFFFF';
  const fontHeading = business?.font_heading || 'Playfair Display, serif';
  const fontBody = business?.font_body || 'Roboto, sans-serif';

  const menuUrl = `${routePrefix}/${deliveryMode || 'delivery'}`;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: bgColor, color: textColor, fontFamily: fontBody }}>
        <div className="sticky top-0 z-10 border-b px-4 py-3" style={{ backgroundColor: bgColor, borderColor: '#333' }}>
          <div className="flex items-center gap-4 max-w-md mx-auto">
            <button onClick={() => navigate(menuUrl)} className="p-2" data-testid="back-to-menu">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold" style={{ fontFamily: fontHeading, color: primaryColor }}>Carrito</h2>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="text-6xl mb-4">🛒</div>
            <p className="opacity-60 mb-4">Tu carrito está vacío</p>
            <Button onClick={() => navigate(menuUrl)} data-testid="back-to-menu-empty" style={{ backgroundColor: primaryColor, color: bgColor }}>
              Ver Menú
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pb-32" style={{ backgroundColor: bgColor, color: textColor, fontFamily: fontBody }}>
      {/* Header */}
      <div className="sticky top-0 z-10 border-b px-4 py-3" style={{ backgroundColor: bgColor, borderColor: '#333' }}>
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(menuUrl)} className="p-2" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: fontHeading, color: primaryColor }}>Carrito</h2>
              <p className="text-xs opacity-60">
                {deliveryMode === 'delivery' ? '🚚 Delivery' : '👜 Take Away'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/${slug}`)}
            className="flex items-center gap-1 text-sm opacity-60 hover:opacity-100 transition-opacity"
            data-testid="cart-home-link"
          >
            Inicio
          </button>
        </div>
      </div>

      {/* Cart Items */}
      <div className="flex-1 px-4 py-6 max-w-md mx-auto w-full">
        <div className="space-y-4">
          {cartItems.map((item) => (
            <div key={item.id} className="rounded-2xl p-4 flex gap-4" style={{ backgroundColor: cardColor }} data-testid={`cart-item-${item.id}`}>
              {item.image_url && (
                <img src={item.image_url} alt={item.name} className="w-20 h-20 object-cover rounded-xl" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold mb-1" style={{ color: primaryColor }}>{item.name}</h3>
                <p className="text-sm opacity-60 mb-2">${item.price.toFixed(2)} c/u</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 rounded-full p-1" style={{ backgroundColor: bgColor }}>
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-all" style={{ backgroundColor: cardColor }} data-testid={`decrease-${item.id}`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-semibold" data-testid={`quantity-${item.id}`}>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-all" style={{ backgroundColor: cardColor }} data-testid={`increase-${item.id}`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="p-2 opacity-50 hover:opacity-100 transition-opacity" data-testid={`remove-${item.id}`}>
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <span className="font-bold text-lg" style={{ color: primaryColor }}>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Summary */}
      <div className="fixed bottom-0 left-0 right-0 border-t px-4 py-4 shadow-lg" style={{ backgroundColor: cardColor, borderColor: '#333' }}>
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex justify-between items-center text-lg">
            <span className="font-semibold">Total</span>
            <span className="text-3xl font-black" style={{ color: primaryColor }} data-testid="cart-total">
              ${getTotal().toFixed(2)}
            </span>
          </div>
          <button
            onClick={() => navigate(`${routePrefix}/fin`)}
            className="w-full h-14 rounded-full text-lg font-bold transition-all hover:scale-105 active:scale-95"
            style={{ backgroundColor: primaryColor, color: bgColor }}
            data-testid="checkout-button"
          >
            Continuar con el Pedido
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartView;
